import React, { useState, useEffect, useRef } from "react";
import Testimonial from "../ui/Testimonial";
// Import Swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, EffectCards } from "swiper/modules";
// Import Lucide icons
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-cards";

const testimonials = [
    {
        id: 1,
        quote: "nyloria a complètement transformé notre présence en ligne. Leur équipe a compris nos besoins dès le départ et a livré un site qui dépasse nos attentes.",
        name: "Sophie Martin",
        position: "Directrice Marketing",
        company: "LuxeRetail",
        image: "/photos/1.avif",
        rating: 5,
    },
    {
        id: 2,
        quote: "Nous avons été impressionnés par la qualité du travail et l'attention aux détails. Notre trafic a augmenté de 150% depuis le lancement du nouveau site.",
        name: "Thomas Dubois",
        position: "CEO",
        company: "Tech Innovate",
        image: "/photos/2.avif",
        rating: 5,
    },
    {
        id: 3,
        quote: "Travailler avec nyloria a été un plaisir du début à la fin. Leur processus est clair, leur communication excellente, et les résultats sont exceptionnels.",
        name: "Claire Fontaine",
        position: "Fondatrice",
        company: "StyleHome",
        image: "/photos/3.avif",
        rating: 5,
    },
    {
        id: 4,
        quote: "Le site e-commerce créé par nyloria nous a permis d'augmenter nos ventes en ligne de 200% en seulement trois mois. Un investissement qui valait vraiment la peine.",
        name: "Marc Leroy",
        position: "Directeur Commercial",
        company: "SportGear",
        image: "/photos/4.avif",
        rating: 4,
    },
];

const Testimonials = () => {
    const [isMobile, setIsMobile] = useState(false);
    const sectionRef = useRef(null);

    // Détecte si on est sur mobile pour des ajustements responsifs
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkIfMobile();
        window.addEventListener("resize", checkIfMobile);

        return () => {
            window.removeEventListener("resize", checkIfMobile);
        };
    }, []);

    // Animation pour les éléments flottants
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!sectionRef.current) return;

            const floatingElements =
                sectionRef.current.querySelectorAll(".floating-element");
            const { clientX, clientY } = e;
            const { width, height } =
                sectionRef.current.getBoundingClientRect();

            floatingElements.forEach((el, index) => {
                const factor = (index + 1) * 0.03;
                const x = (clientX / width - 0.5) * factor * 50;
                const y = (clientY / height - 0.5) * factor * 50;

                el.style.transform = `translate(${x}px, ${y}px)`;
            });
        };

        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    // Add a useEffect for the styles
    useEffect(() => {
        const styleElement = document.createElement("style");
        styleElement.innerHTML = `
            .testimonial-swiper {
                padding: 30px 0;
                width: 100%;
            }
            .swiper-button-next::after,
            .swiper-button-prev::after {
                content: "";
            }
            .testimonial-slide {
                max-width: 600px;
                transform-origin: center center;
                transition: all 0.3s ease;
            }

            .swiper-button-next,
            .swiper-button-prev {
                background: none;
                position: relative;
                width: 40px;
                height: 40px;
                margin: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
            }

            .swiper-button-next::after,
            .swiper-button-prev::after {
                content: "" !important;
                display: none !important;
            }

            .swiper-pagination-bullet {
                background: #cbd5e0;
                opacity: 0.5;
            }

            .swiper-pagination-bullet-active {
                background: var(--primary-500, #4f46e5);
                opacity: 1;
                width: 12px;
                border-radius: 4px;
            }

            /* Hide default swiper navigation buttons */
            .swiper-button-next.swiper-button-disabled,
            .swiper-button-prev.swiper-button-disabled {
                opacity: 0.35;
                cursor: auto;
                pointer-events: none;
            }

            /* Ensure default arrow buttons are hidden */
            .swiper-button-next.swiper-button-hidden,
            .swiper-button-prev.swiper-button-hidden {
                display: none;
            }

            /* Additional styling to ensure default arrows are not visible */
            .swiper-container .swiper-button-prev,
            .swiper-container .swiper-button-next {
                display: none;
            }
        `;
        document.head.appendChild(styleElement);

        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    return (
        <section
            id="testimonials"
            className="pb-20 pt-5 md:pt-40 relative"
            ref={sectionRef}
        >
            {/* Floating elements */}
            <div className="absolute top-40 left-40 w-80 h-80 rounded-full bg-orange-200/20 blur-3xl floating-element"></div>
            <div className="absolute bottom-20 right-1/4 w-64 h-64 rounded-full bg-blue-200/20 blur-2xl floating-element"></div>

            <div className="container mx-auto relative z-10">
                <div className="flex mx-auto justify-center items-center">
                    <div className="mb-16 max-w-2xl text-center">
                        {/* <div className="mb-6">
                            <motion.span
                                className="bg-black/80 text-white text-xs md:text-sm px-6 py-2.5 uppercase tracking-wider font-medium shadow-lg rounded-md"
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                            >
                                TÉMOIGNAGES
                            </motion.span>
                        </div> */}
                        <div>
                            <motion.h2
                                className="text-4xl md:text-5xl font-bold text-center title"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                            >
                                Ce que nos{" "}
                                <span className="text-gradient">clients</span>{" "}
                                disent de nous
                            </motion.h2>
                        </div>
                    </div>
                </div>

                <div className="max-w-4xl mx-auto px-4">
                    <Swiper
                        modules={[Navigation, Pagination, EffectCards]}
                        effect={"cards"}
                        grabCursor={true}
                        centeredSlides={true}
                        slidesPerView={"auto"}
                        cardsEffect={{
                            rotate: 2,
                            perSlideOffset: 12,
                            perSlideRotate: 1,
                            slideShadows: true,
                        }}
                        pagination={{
                            clickable: true,
                            dynamicBullets: true,
                            el: ".swiper-pagination",
                        }}
                        navigation={{
                            nextEl: ".swiper-button-next",
                            prevEl: ".swiper-button-prev",
                            enabled: true,
                            disabledClass: "swiper-button-disabled",
                            hiddenClass: "swiper-button-hidden",
                        }}
                        className="testimonial-swiper"
                    >
                        {testimonials.map((testimonial) => (
                            <SwiperSlide
                                key={testimonial.id}
                                className="testimonial-slide rounded-xl shadow-lg"
                            >
                                <Testimonial {...testimonial} />
                            </SwiperSlide>
                        ))}

                        <div className="swiper-controls mt-10 flex justify-center items-center gap-6">
                            <button
                                className="swiper-button-prev w-[40px] h-[40px] min-w-[40px] min-h-[40px] rounded-full bg-white border border-gray-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all shadow-sm hover:shadow cursor-pointer"
                                aria-label="Témoignage précédent"
                            >
                                <ChevronLeft
                                    size={16}
                                    className="text-secondary-700"
                                />
                            </button>

                            <button
                                className="swiper-button-next w-[40px] h-[40px] min-w-[40px] min-h-[40px] rounded-full bg-white border border-gray-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all shadow-sm hover:shadow cursor-pointer"
                                aria-label="Témoignage suivant"
                            >
                                <ChevronRight
                                    size={16}
                                    className="text-secondary-700"
                                />
                            </button>
                        </div>
                    </Swiper>
                </div>
            </div>
        </section>
    );
};

export default Testimonials;
